import AirlineSeatReclineNormalIcon from '@mui/icons-material/AirlineSeatReclineNormal'
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter'
import EventBusyIcon from '@mui/icons-material/EventBusy'
import EventRepeatIcon from '@mui/icons-material/EventRepeat'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import LuggageIcon from '@mui/icons-material/Luggage'
import RestaurantIcon from '@mui/icons-material/Restaurant'
import { Box, Divider, IconButton, Stack, Typography } from '@mui/material'
import React from 'react'

import { PerAdultLabel } from '@/src/components/common/srp/perAdultLabel'
import ExclusiveBenefitNote from '@/src/components/desktop/srp/fareFamily/fareFamilyBody/ExclusiveBenefitNote'
import { Plan } from '@/src/components/desktop/srp/fareFamily/types'
import useSearchResults from '@/src/hooks/srp/useSearchResults'
import { RadioButtonChecked, RadioButtonUnchecked } from '@mui/icons-material'
import { useTranslations } from 'next-intl'
import PremiumBadge from './badge'

const getBenefitIcon = (key: string, isIncluded: boolean) => {
    const iconProps = {
        sx: {
            fontSize: '20px',
            color: isIncluded ? '#16a34a' : '#dc2626',
            flexShrink: 0
        }
    }

    switch (key) {
        case 'checkinBaggage':
            return <LuggageIcon {...iconProps} />
        case 'cabinBaggage':
            return <BusinessCenterIcon {...iconProps} />
        case 'cancel':
            return <EventBusyIcon {...iconProps} />
        case 'amend':
            return <EventRepeatIcon {...iconProps} />
        case 'seat':
            return <AirlineSeatReclineNormalIcon {...iconProps} />
        case 'meal':
            return <RestaurantIcon {...iconProps} />
        default:
            return null
    }
}

type FareFamilyBodyProps = {
    filteredPlans: { plan: Plan; planIndex: number }[]
    selectedFareKey: string | null
    setSelectedFareKey: (fareKey: string) => void
    onInfoClick?: (plan: Plan) => void
}

const FareFamilyBody = ({ filteredPlans, selectedFareKey, setSelectedFareKey, onInfoClick }: FareFamilyBodyProps) => {
    const { selectedJourneyLeg } = useSearchResults()
    const t = useTranslations('common')
    return (
        <Stack direction="column" spacing={2}>
            {filteredPlans.map(({ plan, planIndex }) => {
                const benefitEntries = Object.entries(plan.benefitsText || {})

                return (
                    <Box
                        key={plan.fareKey + planIndex}
                        sx={{
                            position: 'relative',
                            border: (theme) => selectedFareKey === plan.fareKey ? `1px solid ${theme.palette.primary.main}` : '1px solid #e0e0e0',
                            borderRadius: 2,
                            backgroundColor: '#fff',
                            cursor: 'pointer',
                            overflow: 'visible',
                            transition: 'border 0.2s ease'
                        }}
                        onClick={() => setSelectedFareKey(plan.fareKey)}
                    >
                        {plan.badge && <PremiumBadge sx={{ marginLeft: '12px' }} badgeText={plan.badge} />}
                        <Box px={2} py={2}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Typography fontWeight={700} fontSize={14}>
                                        {plan.title}
                                    </Typography>
                                    {onInfoClick && (
                                        <IconButton
                                            size="small"
                                            onClick={e => {
                                                e.stopPropagation()
                                                onInfoClick(plan)
                                            }}
                                            sx={{
                                                padding: 0.5,
                                                '&:hover': {
                                                    backgroundColor: 'rgba(30, 99, 208, 0.08)'
                                                }
                                            }}
                                        >
                                            <InfoOutlinedIcon sx={{ fontSize: 18, }} color='primary' />
                                        </IconButton>
                                    )}
                                </Stack>
                                {selectedFareKey === plan.fareKey ? <RadioButtonChecked color='primary' /> : <RadioButtonUnchecked color='action' />}

                            </Stack>
                            <Box display="flex" gap={1} alignItems="center" width="100%" mb={2}>
                                <Typography fontSize={24} fontWeight={700} lineHeight="1rem">
                                    {!!plan.planPricePrefix && plan.planPricePrefix} {plan.displayPrice}{' '}
                                    {plan.isPerPaxPricingDefined && <PerAdultLabel />}
                                </Typography>
                                {!!plan.showTotalRTPriceMessage && (
                                    <Typography
                                        variant="caption"
                                        fontWeight={600}
                                        color="#484747"
                                        border={'1px solid #cac8c8'}
                                        borderRadius={0.5}
                                        px={0.5}
                                    >
                                        {t('roundTripTotalPrice')}
                                    </Typography>
                                )}
                            </Box>

                            {benefitEntries.map(([key, value], i) => {
                                const isIncluded = plan.included?.[key] ?? false
                                const coloredIcon = getBenefitIcon(key, isIncluded)
                                const lastBenefit = i === benefitEntries.length - 2
                                if (key === 'exclusiveBenefits' && selectedJourneyLeg == 1) return null
                                return (
                                    value &&
                                    (key === 'exclusiveBenefits' ? (
                                        <ExclusiveBenefitNote key={key} note={value} />
                                    ) : (
                                        <React.Fragment key={key}>
                                            <Divider />
                                            <Stack
                                                direction="row"
                                                justifyContent="space-between"
                                                alignItems="center"
                                                py={1.3}
                                            >
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    {coloredIcon}
                                                    <Typography fontSize={14} color="#1a1a1a">
                                                        {value}
                                                    </Typography>
                                                </Stack>
                                            </Stack>
                                            {lastBenefit && selectedJourneyLeg == 0 && (
                                                <Divider sx={{ marginBottom: 1.5 }} />
                                            )}
                                        </React.Fragment>
                                    ))
                                )
                            })}
                        </Box>
                    </Box>
                )
            })}
        </Stack>
    )
}

export default FareFamilyBody
